import type {
  UrlContextConfig,
  UrlContextPayload,
  UrlContextSnapshot,
  UrlContextSource,
  UrlManagedParamConfig,
} from '@/config/shared/url-context';
import type { StorageService } from '@/services/storage/types';

import { ForwardParamResolver } from './ForwardParamResolver';
import { LinkDecorator } from './LinkDecorator';
import { ManagedParamResolver } from './ManagedParamResolver';
import { QueryParamCollector } from './QueryParamCollector';
import { resolveUrlContextConfig } from './resolve-config';
import { UrlContextStore } from './UrlContextStore';
import { freezeRecord } from './utils';
/**
 * Orquestra UrlContext: collect → manage → forward → decorate → snapshot.
 * SSR-safe; init/destroy idempotentes.
 */
export class UrlContextService {
  private readonly storage: StorageService | null;

  private readonly rawConfig: UrlContextConfig | undefined;

  private readonly collector = new QueryParamCollector();

  private readonly forwardResolver = new ForwardParamResolver();

  private store: UrlContextStore | null = null;

  private managedResolver: ManagedParamResolver | null = null;

  private decorator: LinkDecorator | null = null;

  private snapshot: UrlContextSnapshot = emptySnapshot();

  private managedConfigs: readonly UrlManagedParamConfig[] = [];

  private decorateManaged: Record<string, string> = {};

  private initialized = false;

  private enabled = false;

  constructor(
    config: UrlContextConfig | undefined,
    storage?: StorageService | null
  ) {
    this.rawConfig = config;
    this.storage = storage ?? null;
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    const resolved = resolveUrlContextConfig(this.rawConfig);
    this.enabled = resolved.enabled;
    this.managedConfigs = resolved.managedParams;

    if (!resolved.enabled) {
      this.snapshot = emptySnapshot();
      return;
    }

    this.store = new UrlContextStore(this.storage);
    this.managedResolver = new ManagedParamResolver(this.store);

    const queryParams = this.collector.collect(resolved.queryParams);
    const managed = this.managedResolver.resolve(resolved.managedParams);

    // Após replaceState, query params podem já incluir sid — re-coleta leve
    const queryAfter =
      Object.keys(managed.values).length > 0
        ? this.collector.collect(resolved.queryParams)
        : queryParams;

    const forwarded = this.forwardResolver.resolve({
      queryParams: queryAfter,
      managedValues: managed.values,
      config: resolved.forwardParams,
    });

    const source: Record<string, UrlContextSource> = {
      ...managed.sources,
    };
    for (const key of Object.keys(forwarded)) {
      if (!source[key]) {
        source[key] = 'url';
      }
    }

    const urlContext: UrlContextPayload = {
      ...(Object.keys(managed.values).length > 0
        ? { managed: freezeRecord(managed.values) }
        : {}),
      ...(Object.keys(forwarded).length > 0
        ? { forwarded: freezeRecord(forwarded) }
        : {}),
      ...(Object.keys(source).length > 0
        ? { source: Object.freeze({ ...source }) }
        : {}),
    };
    this.snapshot = {
      queryParams: freezeRecord(queryAfter),
      urlContext: Object.freeze(urlContext),
    };

    const decorateForwarded = freezeRecord(forwarded);
    this.decorateManaged = { ...managed.decorateParams };

    const hasSid = resolved.managedParams.some(
      p => p.param === 'sid' && p.decorateLinks !== false
    );

    this.decorator = new LinkDecorator({
      forwardedParams: decorateForwarded,
      managedParams: freezeRecord(this.decorateManaged),
      config: resolved.linkDecoration,
      includeSidExclude: hasSid,
    });
    this.decorator.start();
  }

  /**
   * Define ou substitui um managed param após o init (ex.: `oid` no checkout).
   * Idempotente para o mesmo valor. Persistência e decoração seguem a config do param.
   *
   * @returns false se UrlContext desabilitado, não inicializado ou param não configurado.
   */
  setManagedParam(param: string, value: string): boolean {
    if (!this.initialized || !this.enabled || !this.managedResolver) {
      return false;
    }

    const key = param.trim();
    const trimmed = value.trim();
    if (!key || !trimmed) {
      return false;
    }

    const config = this.managedConfigs.find(c => c.param === key);
    if (!config) {
      return false;
    }

    const current = this.snapshot.urlContext.managed?.[key];
    if (current === trimmed) {
      return true;
    }

    this.managedResolver.applyExternalValue(config, trimmed);

    const managed = {
      ...(this.snapshot.urlContext.managed ?? {}),
      [key]: trimmed,
    };
    const source = {
      ...(this.snapshot.urlContext.source ?? {}),
      [key]: 'manual' as const,
    };

    // Re-coleta query após possível replaceState
    const resolved = resolveUrlContextConfig(this.rawConfig);
    const queryAfter = this.collector.collect(resolved.queryParams);

    this.snapshot = {
      queryParams: freezeRecord(queryAfter),
      urlContext: Object.freeze({
        managed: freezeRecord(managed),
        ...(this.snapshot.urlContext.forwarded
          ? { forwarded: freezeRecord(this.snapshot.urlContext.forwarded) }
          : {}),
        source: Object.freeze({ ...source }),
      }),
    };

    if (config.decorateLinks !== false) {
      this.decorateManaged = { ...this.decorateManaged, [key]: trimmed };
      this.decorator?.updateManagedParams(this.decorateManaged);
      // Se decorator ainda não startou (sem params no init), cria agora
      if (!this.decorator && resolved.enabled) {
        const hasSid = this.managedConfigs.some(
          p => p.param === 'sid' && p.decorateLinks !== false
        );
        this.decorator = new LinkDecorator({
          forwardedParams: freezeRecord(
            this.snapshot.urlContext.forwarded ?? {}
          ),
          managedParams: freezeRecord(this.decorateManaged),
          config: resolved.linkDecoration,
          includeSidExclude: hasSid,
        });
        this.decorator.start();
      }
    }

    return true;
  }

  destroy(): void {
    this.decorator?.destroy();
    this.decorator = null;
    this.managedResolver = null;
    this.store = null;
    this.managedConfigs = [];
    this.decorateManaged = {};
    this.initialized = false;
    this.enabled = false;
    this.snapshot = emptySnapshot();
  }

  /** Alias de destroy — cleanup completo sem listeners/refs. */
  dispose(): void {
    this.destroy();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getSnapshot(): UrlContextSnapshot {
    return {
      queryParams: freezeRecord(this.snapshot.queryParams),
      urlContext: {
        ...(this.snapshot.urlContext.managed
          ? { managed: freezeRecord(this.snapshot.urlContext.managed) }
          : {}),
        ...(this.snapshot.urlContext.forwarded
          ? { forwarded: freezeRecord(this.snapshot.urlContext.forwarded) }
          : {}),
        ...(this.snapshot.urlContext.source
          ? {
              source: Object.freeze({
                ...this.snapshot.urlContext.source,
              }),
            }
          : {}),
      },
    };
  }
  getQueryParams(): Record<string, string> {
    return { ...this.snapshot.queryParams };
  }

  getUrlContext(): UrlContextPayload {
    return this.getSnapshot().urlContext;
  }
}

function emptySnapshot(): UrlContextSnapshot {
  return {
    queryParams: freezeRecord({}),
    urlContext: Object.freeze({}),
  };
}
